import { ApolloError } from '@apollo/client';
import React, { forwardRef, useEffect, useState } from 'react';
import { Button, Alert, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import { IAddressLight } from '../../../types/contact';
import { IOrgLight, ISearchOrgInput } from '../../../types/org';
import AddressTypeahead from '../../common/typeahead/address-typeahead';
import TopOrganizationTypeahead from '../../common/typeahead/top-organization-typeahead';
import { SearchIcon } from '../../common/icons';
import { join } from '../../../utils/commons';

type SearchOrgFormProp={
  loading:boolean
  error:ApolloError | undefined
  onSearch:(input:ISearchOrgInput,isValid: boolean)=>void
  onChange: ()=>void
}

export type SearchOrgFormRef = {
  getInput:() => ISearchOrgInput;
};

const SearchOrgForm = forwardRef<SearchOrgFormRef,SearchOrgFormProp>((props, ref)=> {

  const { loading, error, onChange, onSearch } = props;

  const [selectedOrgId, setSelectedOrgId] = useState<number>(0);
  const [valid, setValid] = useState(true);
  const [errors, setErrors] = useState({org:'', addressFullTxt:''});
  // note: using state for this is not ideal, but we need to see if we need to wait for the value to update
  const [selectedAddress, setSelectedAddress] = useState<IAddressLight|undefined>(undefined);

  // clean any dashes / spaces
  const normalizeContactNumberRegex = /[- ]/g;

  useEffect(()=>{
    setValid(!errors.org && !errors.addressFullTxt);
  },[errors])

  const onSearchFieldChange = (event: React.FormEvent<HTMLInputElement>) => {
    onChange();
  }

  const onAddressSelected = (address: IAddressLight | undefined) => {
    setSelectedAddress(address);
    onChange();
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clear();

    if (errors.org || errors.addressFullTxt) {
      // invalid form, exit before searching
      return;
    }

    const form = event.target as HTMLFormElement;

    const addressFullTxtElement = (form.querySelector("#addressFullTxt") as HTMLInputElement);

    const searchInput: ISearchOrgInput = {
      orgId: selectedOrgId,
      orgName: (form.querySelector("#orgName") as HTMLInputElement).value.trim(),
      addressFullTxt: addressFullTxtElement.value.trim(),
      contactNumber: (form.querySelector("#contactNumber") as HTMLInputElement).value.trim().replace(normalizeContactNumberRegex, ''),
      unitDescription: (form.querySelector("#unitDescription") as HTMLInputElement).value.trim()
    };

    // there are no errors, but check if the fields have data
    const isEmpty = searchInput.orgName === ''
      && searchInput.addressFullTxt === ''
      && searchInput.contactNumber === ''
      && searchInput.unitDescription === '';

    // there is a potential race condition if the user pressed enter on the non-standard type-ahead list
    // the form may submit before the address is populated by the event handler
    if (searchInput.addressFullTxt) {
      // address has a value, wait 10ms and update to what is in the state after the update
      setTimeout(() => {
        if (selectedAddress && selectedAddress.addressTxt !== searchInput.addressFullTxt) {
          searchInput.addressFullTxt = join(selectedAddress.addressTxt, selectedAddress.addressTxt2);
          onSearch(searchInput, !isEmpty);
        }
      }, 10);
    }
    else {
      onSearch(searchInput, !isEmpty);
    }
  }

  const handleReset = () => {
    clear();
  };

  const clear = () => {
    setValid(true);
    onChange();
  };

  // error only occurs if the user types something that is not in the typeahead list
  const orgSelectHandle = (org: IOrgLight | undefined) => {
    const orgId = org?.id || 0;
    setSelectedOrgId(orgId);

    setErrors((prevErrors) => ({ ...prevErrors, org: '' }));
    onChange();
  }

  // error only occurs if the user types something that is not in the typeahead list
  const addressFullTxtValidate = (error: string) => {
    setErrors(prev => {
    if (prev.addressFullTxt !== error) {
      return { ...prev, addressFullTxt: error };
    }
    return prev;
  });
  }

  const orgValidate = (error: string) => {
    setErrors(prev => ({ ...prev, org: error }));
  }



  return (

    <Form onSubmit={handleSubmit} onReset={handleReset}>
      <Row>
        <Col md={6}>
          <FormGroup row>
            <Label for="top-org" sm={4}>
              Under Top Level:
            </Label>
            <Col sm={8}>
              <TopOrganizationTypeahead elementId="top-org" orgSelectHandle={orgSelectHandle} validate={orgValidate}/>
            </Col>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup row>
            <Label for="orgName" sm={4}>Name:</Label>
            <Col sm={8}>
              <Input id="orgName" name="orgName" placeholder="Enter a full or partial organization name..." onChange={onSearchFieldChange}/>
            </Col>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup row>
            <Label for="unitDescription" sm={4}>Unit Description Keyword:</Label>
            <Col sm={8}>
              <Input id="unitDescription" name="unitDescription" placeholder="Enter a keyword to search organization descriptions" onChange={onSearchFieldChange}/>
            </Col>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup row>
            <Label for="addressFullTxt" sm={4}>Address:</Label>
            <Col sm={8}>
              <AddressTypeahead id="addressFullTxt" addressSelected={onAddressSelected} error={errors.addressFullTxt} validate={addressFullTxtValidate} includeInactive={true}/>
              </Col>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup row>
            <Label for="contactNumber" sm={4}>Phone/Fax Number:</Label>
            <Col sm={8}>
              <Input id="contactNumber" name="contactNumber" placeholder="Enter a ten-digit phone or fax number" onChange={onSearchFieldChange} type="tel" />
            </Col>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col className="oms-row right-align-col">
          <Button type="submit" color="primary" disabled={!valid}>
            <SearchIcon /> Search
          </Button>
        </Col>
      </Row>
      {error && <Alert color="danger">Error searching orgs.</Alert>}
    </Form>

  )
});

export default SearchOrgForm
